// Copyright (c) 2020 WiseTime. All rights reserved.

import { createGlobalStyle, css, FlattenSimpleInterpolation } from "styled-components/macro"
import { normalize } from "styled-normalize"
import {
  GROUPS_CONTENT_CLASSNAME,
  HEADER_CLASSNAME,
  PAGE_CLASSNAME,
  RESTRICTED_OVERLAY_CLASSNAME, TIMESHEET_FIELD_DIALOG_CLASSNAME,
} from "../features/shared/classNames"
import { lighten } from "polished"

const groupDisplayShadow = "0 3px 3px rgba(0, 0, 0, .006)," +
  "3px 3px 3px rgba(0, 0, 0, .006)," +
  "-3px 3px 3px rgba(0, 0, 0, .006)"

/*
 * colors variables naming:
 * https://www.color-blindness.com/color-name-hue/ or
 * http://chir.ag/projects/name-that-color/
 */
export const theme = {
  colors: {
    aqxDark: "#2f2d42",
    black: "#000",
    nero: "#222",
    mineShaft: "#3d3d3d",
    charcoal: "#444",
    mortar: "#555",
    dimGray: "#656565",
    grey: "#828282",
    dustyGray: "#959595",
    nobel: "#9b9b9b",
    edward: "#9c9d9d",
    manatee: "#888fa4",
    grayChateau: "#959ea9",
    darkGray: "#a3a3a3",
    silverChalice: "#b1b1b1",
    silverSand: "#b9b7b1",
    silver: "#cbcbcb",
    gainsboro: "#dcdcdc",
    mercury: "#e9e9e9",
    athensGray: "#eff1f5",
    gallery: "#ededed",
    seashell: "#f1f1f1",
    wildSand: "#f6f6f6",
    alabaster: "#f9f9f9",
    whiteSmoke: "#f4f4f4",
    white: "#fff",
    indigo: "#5949e8",
    portage: "#a98cf1",
    perano: "#a4ace4",
    melrose: "#a9adfb",
    moonRaker: "#d1daf8",
    lavender: "#d9e0fa",
    quartz: "#d9dff2",
    linkWater: "#e0e7f9",
    hawkesBlue: "#ebedfe",
    selago: "#f1f3fe",
    zircon: "#f8faff",
    mexicanRed: "#a12525",
    fireBrick: "#de0c0c",
    cinnabar: "#ec4c4c",
    bittersweet: "#fc6b6b",
    lightCoral: "#f17a7a",
    sweetPink: "#fd9d9d",
    sundown: "#ffb0b0",
    yourPink: "#ffc0c0",
    mistyRose: "#ffe5e5",
    pippin: "#ffe3e3",
    wispPink: "#fceaea",
    tropicalRainForest: "#00805a",
    fruitSalad: "#579868",
    chateauGreen: "#3ba343",
    malachite: "#0fbf64",
    mantis: "#6bc463",
    gossip: "#8ed288",
    apple: "#39cb2c",
    emerald: "#3bd040",
    pastelGreen: "#83e27b",
    magicMint: "#c1f9c5",
    terracotta: "#e36a58",
    flamenco: "#ff7e10",
    mangoTango: "#d87a00",
    rawSienna: "#cd7f3b",
    texasRose: "#fea558",
    papayaWhip: "#fff1e1",
    oldlace: "#fefcf7",
    shipCove: "#7180a8",
    lightYellow: "#ffffe0",
    iconColor: "#888b9a",
    labelColor: "#b5b4b4",
    inputBorder: "rgba(47, 45, 66, 0.6)",
    inputFocus: "#7c6fee",
    dangerColor: "#cf0025",
  },
  fontFamily: "'Inter', 'Avenir Next', Avenir, Helvetica, Arial, sans-serif",
  wtSidebarWidth: "16rem",
  headerHeight: "9.4rem",
  groupColumnWidht: "41rem",
  dropdownContentMaxHeight: 300,
  searchDropdownInputHeight: 40,
  searchDropdownListHeaderHeight: 30,
  searchDropdownListHeight: 270,
  AddToGroupListHeaderHeight: 32,
  AddToGroupListItemHeight: 60,
  tilesDrawerWidth: "370px",
  tileSize: 40,
  tilesPerRow: 7,
  iconSizeInButton: "18px",
  timesheetTableMinWidth: "734px",
  timesheetRowMinHeight: "34px",
  tileShadow: "0 0 5px rgba(0, 0, 0, .1)",
  paperShadow: "0 1px 30px rgba(0, 0, 0, .03)",
  cellShadow: "0 4px 12px rgba(0, 0, 0, .1)",
  actionPaneShadow: "0 0 10px rgba(0, 0, 0, .15)",
  messageShadow: "0 4px 8px rgba(47, 45, 66, .1)",
  groupDisplayShadow,
  groupDisplayShadowFirstElement: "0 -3px 3px rgba(0, 0, 0, .006)," + groupDisplayShadow,
  expectationColor: "#83a895",
  screen: {
    xxs: "25em", // em = px / 16
    xxsNumeric: 400,
    xs: "35.5em",
    xsNumeric: 568,
    sm: "47.875em",
    smNumeric: 766,
    md: "62em",
    mdNumeric: 992,
    lg: "80em",
    lgNumeric: 1280,
    lgToolbar: "83.125em",
    lgNumericToolbar: 1330,
    xlg: "85.375em",
    xlgNumeric: 1366,
    xxlg: "100em",
    xxlgNumeric: 1600,
    // Specific UI elements
    xlgToolbar: "88.75em",
    xlgNumericToolbar: 1420,
  }
}

export type ThemeType = typeof theme

const createTimeBlockProgressStyles = (
  barColor: string,
  backColor: string,
): FlattenSimpleInterpolation => {
  let styles = `
    position: relative;
    width: 17px;
    height: 17px;
    min-width: 17px;
    border-radius: 50%;
    border: 1px solid ${backColor};
    background-color: ${barColor};
  `
  const step = 5
  const loops = 100 / step
  const increment = 360 / loops
  const half = loops / 2
  for (let i = 0; i < loops; i += 1) {
    const degrees = i < half ?
      `${90 + (increment * i)}deg` :
      `${-90 + (increment * (i - half))}deg`
    const backgroundImage = i < half ?
      `linear-gradient(90deg, ${backColor} 50%, transparent 50%, transparent), linear-gradient(${degrees}, ${barColor} 50%, ${backColor} 50%, ${backColor})` :
      `linear-gradient(${degrees}, ${barColor} 50%, transparent 50%, transparent), linear-gradient(270deg, ${barColor} 50%, ${backColor} 50%, ${backColor})`
    styles += `
       &[data-progress="${i * step}"] {
         background-image: ${backgroundImage};
       }
    `
  }
  return css`${styles}`
}

export const GlobalStyles = createGlobalStyle<{ theme: ThemeType }>`
  
  ${normalize}

  *:before,
  *:after,
  * {
    box-sizing: border-box;
  }
  
  html {
    font-size: 62.5%;
  }
  
  body {
    font-family: ${({ theme }): string => theme.fontFamily};
    font-size: 1.2rem;
    font-weight: 400;
    line-height: normal;
    color: ${({ theme }): string => theme.colors.dimGray};
    background-color: ${({ theme }): string => theme.colors.wildSand};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  // Custom scrollbar from WT console
  // Always show scrollbars in Chrome/Safari on Mac
  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
    height: 7px;
  }
  
  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, .09);
    -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, .09);
  }
  
  ::placeholder,
  ::-webkit-input-placeholder {
    color: ${({ theme }): string => theme.colors.dustyGray};
  }
  
  :-ms-input-placeholder {
     color: ${({ theme }): string => theme.colors.dustyGray};
  }
  
  ::-webkit-input-placeholder {
    line-height: normal; // Fix Safari placeholder line-height
  }
  
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 0;
    margin-bottom: 0;
  }
  
  h1 {
    font-size: 2.2rem;
    font-weight: 600;
    margin-bottom: 2rem;
  }
  
  .h1 {
    font-size: 5rem;
    line-height: 58px;
  }
  
  h2 {
    font-size: 2rem;
    font-weight: 600;
    line-height: 26px;
  }
  
  h3 {
    font-size: 1.8rem;
    font-weight: 600;
  }
  
  h4 {
    font-size: 1.6rem;
    line-height: 27px;
    margin-bottom: .6rem;
  }

  body.focus-using-mouse :focus {
    outline: none;
  }
  
  *:focus {
    outline: ${({ theme }) => theme.colors.portage} auto 2px;
  }
  
  h5 {
    font-size: 1.4rem;
    font-weight: 600;
    line-height: 28px;
  }
  
  h6 {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: .6rem;
  }
  
  button,
  input,
  optgroup,
  select,
  textarea {
    font-family: ${({ theme }): string => theme.fontFamily};
  }
  
  button {
    cursor: pointer;
    border: 0;
    padding: 0;
    color: inherit;
    background-color: transparent;
  }
  
  a {
    color: inherit;
    text-decoration: none;
    cursor: pointer;
    transition: all .3s;
  }
  
  p {
    line-height: 26px;
    margin-top: 0;
    margin-bottom: 1.6rem;
  }
  
  b {
    font-weight: 700;
  }
  
  dl,
  ol,
  ul {
    list-style-position: outside;
    padding-left: 0;
  }
  
  img {
    max-width: 100%;
    height: auto;
  }
  
  svg {
    display: inline-block;
    vertical-align: top;
    width: 100%;
    max-width: 100%;
  }
  
  .icon-svg {
    display: inline-block;
    vertical-align: top;
    width: 1.6rem;
    height: 1.6rem;
    min-width: 1.6rem;
    font-size: inherit;
    fill: currentColor;
  }
  
  .svg-inline-width {
    width: auto;
  }
  
  pre {
    white-space: pre-wrap;
    display: block;
    font-size: 1.2rem;
    padding: 1rem;
    background-color: ${({ theme }): string => theme.colors.alabaster};
    border: 1px solid ${({ theme }): string => theme.colors.mercury};
  }
  
  [disabled] {
    pointer-events: none;
    cursor: default;
  }
  
  [hidden],
  .hidden {
    display: none !important;
  }
  
  [readonly] {
    opacity: .7;
    pointer-events: none;
  }
  
  .hr {
    display: block;
    height: 1px;
    border: 0;
    background-color: ${({ theme }): string => theme.colors.mercury};
    padding: 0;
    margin: 0;
  }
  
  .Toastify__toast-container {
    color: ${({ theme }): string => theme.colors.black};
    display: flex;
    flex-direction: column;
    
    @media (max-width: ${({ theme }) => theme.screen.xs}) {
      width: 100%;
    }
  }

  .Toastify__toast {
    align-self: center;
    font-family: ${({ theme }): string => theme.fontFamily};
    font-size: 1.6rem;
    line-height: 1.5;
    white-space: pre-line;
    padding: 10px;
    min-height: 0;
    background-color: ${({ theme }): string => theme.colors.mercury};
    border: 1px solid ${({ theme }): string => theme.colors.silverSand};
    box-shadow: none;
    cursor: initial;
    
    &.snackbar--text-align-left {
      text-align: left;
    }
  }
  
  .Toastify__toast-container--top-center {
    text-align: center;
    top: 0;
    padding: 0;

    .Toastify__toast {
      margin-top: -1px;
    }

    @media (min-width: ${({ theme }) => theme.screen.xs}) {
      width: auto;
    }
  }
  
  .Toastify__toast-container--top-right {
    .Toastify__toast {
      @media (max-width: ${({ theme }) => theme.screen.xs}) {
        border-radius: 0;
      }
    }
    
    @media (min-width: ${({ theme }) => theme.screen.sm}) {
      width: 388px;
    }
  }
  
  .Toastify__toast-body {
    width: 100%;
  }
  
  .Toastify__toast--info {
    background-color: ${({ theme }): string => theme.colors.zircon};
    border-color: ${({ theme }): string => theme.colors.indigo};
  }
  
  .Toastify__toast--success {
    background-color: ${({ theme }): string => theme.colors.magicMint};
    border-color: ${({ theme }): string => theme.colors.chateauGreen};
  }
  
  .Toastify__toast--error {
    background-color: ${({ theme }): string => theme.colors.mistyRose};
    border-color: ${({ theme }): string => theme.colors.fireBrick};
  }
  
  .Toastify__toast--warning {
    background-color: ${({ theme }): string => theme.colors.papayaWhip};
    border-color: ${({ theme }): string => theme.colors.mangoTango};
  }

  // pulltorefreshjs
  .ptr--ptr {
    box-shadow: none !important;
  }

  .ptr--text {
    font-size: 1.2rem;
    font-weight: 500;
  }

  @keyframes rotateHand {
    to {
      transform: rotate(360deg); transform-origin: 43px 86px 0;
    }
  }

  .svg-minutes-hand {
    transform-origin: 43px 86px 0;
    animation: rotateHand 16s steps(60, end) infinite;
  }

  .svg-seconds-hand {
    transform-origin: 43px 86px 0;
    animation: rotateHand 1s steps(60, end) infinite;
  }
  
  .time-block-progress {
    ${({ theme }): FlattenSimpleInterpolation => createTimeBlockProgressStyles(theme.colors.melrose, theme.colors.lavender)};
    margin-left: 10px;
  }

  @media (min-width: ${({ theme }) => theme.screen.md}) {
    .wt-sidebar--collapsed {
      ${`.${PAGE_CLASSNAME}`} {
        padding-left: 6rem;
      }
      ${`.${HEADER_CLASSNAME}`},
      ${`.${GROUPS_CONTENT_CLASSNAME}`},
      ${`.${TIMESHEET_FIELD_DIALOG_CLASSNAME}`} {
        left: 6rem;
      }
      ${`.${RESTRICTED_OVERLAY_CLASSNAME}`} {
        left: 6rem;
        width: calc(100vw - 6rem);
      }
    }
  }

  .horizontal-slider {
    width: 100%;
    height: 30px;
  }

  .slider-track {
    cursor: pointer;
    top: 0;
    height: 30px;
    border-top: 12px solid ${({ theme }): string => theme.colors.white};
    border-bottom: 12px solid ${({ theme }): string => theme.colors.white};

    &.slider-track-0 {
      background: ${({ theme }): string => theme.colors.indigo};
      border-radius: 2px 0 0 2px;
    }

    &.slider-track-1 {
      background: ${({ theme }) => lighten(0.3, theme.colors.indigo)};
      border-radius: 0 2px 2px 0;
    }
  }

  .slider-thumb {
    font-size: 1.2rem;
    font-weight: 700;
    text-align: center;
    background-color: ${({ theme }): string => theme.colors.indigo};
    color: ${({ theme }): string => theme.colors.white};
    cursor: pointer;
    top: 2px;
    width: 40px;
    height: 25px;
    line-height: 25px;
    border-radius: 4px;
    padding: 0 2px;

    &:active,
    &:focus {
      outline: none;
      box-shadow: 0 0 0 5px rgba(${({ theme }): string => theme.colors.indigo}, .1);
    }
  }
`
